import React, { useState, useEffect } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import StatCard from "./StatCard";

// Importar as 4 imagens
const imagens = [
  new URL("../assets/image6.jpeg", import.meta.url).href,
  new URL("../assets/image7.jpeg", import.meta.url).href,
  new URL("../assets/image2.jpeg", import.meta.url).href,
  new URL("../assets/image3.jpeg", import.meta.url).href,
];

interface Stat {
  value: string;
  label: string;
}

function About() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const stats: Stat[] = [
    { value: "100+", label: "Clientes Satisfeitos" },
    { value: "8", label: "Tipos de Serviços" },
    { value: "10+", label: "Anos de Experiência" },
  ];

  // Autoplay - troca a cada 4 segundos
  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % imagens.length);
      }, 4000);

      return () => clearInterval(interval);
    }
  }, [isPaused]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % imagens.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + imagens.length) % imagens.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <section
      id="sobre"
      className="py-20 bg-gradient-to-b from-gray-50 to-white"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 text-sd-dark">
            Sobre SD Serviços e Manutenções
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Profissional experiente com mais de 10 anos de experiência. Qualidade garantida em todos os serviços.
          </p>

          {/* Layout com Imagem (Carrossel) e Texto */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-16">
            {/* CARROSSEL DE IMAGENS */}
            <div 
              className="relative group"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="relative overflow-hidden rounded-2xl shadow-2xl min-h-[400px] md:min-h-[500px]">
                {/* Imagens do Carrossel */}
                {imagens.map((imagem, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                      index === currentSlide
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-105"
                    }`}
                  >
                    <img
                      src={imagem}
                      alt={`SD Serviços - Imagem ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}

                {/* Overlay Gradiente */}
                <div className="absolute inset-0 bg-gradient-to-t from-sd-dark/60 via-transparent to-transparent z-10"></div>

                {/* Botões de Navegação */}
                <button
                  onClick={prevSlide}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 z-20 opacity-0 group-hover:opacity-100"
                  aria-label="Anterior"
                >
                  <FaChevronLeft className="text-sd-dark text-lg" />
                </button>

                <button
                  onClick={nextSlide}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 z-20 opacity-0 group-hover:opacity-100"
                  aria-label="Próximo"
                >
                  <FaChevronRight className="text-sd-dark text-lg" />
                </button>

                {/* Indicadores (Dots) */}
                <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                  {imagens.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`transition-all duration-300 rounded-full ${
                        index === currentSlide
                          ? "w-8 h-2 bg-sd-orange"
                          : "w-2 h-2 bg-white/60 hover:bg-white"
                      }`}
                      aria-label={`Ir para imagem ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Badge Flutuante */}
                <div className="absolute bottom-6 left-6 right-6 z-20">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-sd-orange rounded-full flex items-center justify-center flex-shrink-0">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="font-bold text-sd-dark text-sm">
                          Serviço com Garantia
                        </p>
                        <p className="text-xs text-gray-600">
                          Profissional qualificado
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contador de Slides */}
                <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-semibold z-20">
                  {currentSlide + 1} / {imagens.length}
                </div>
              </div>

              {/* Decoração - Círculos */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-sd-orange/20 rounded-full blur-2xl -z-10"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-500/20 rounded-full blur-2xl -z-10"></div>
            </div>

            {/* Conteúdo de Texto */}
            <div className="flex flex-col justify-center">
              <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-gray-100">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 bg-sd-orange/10 text-sd-orange px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Empresa Referência em Serviços
                </div>

                <h3 className="text-2xl md:text-3xl font-bold text-sd-dark mb-4">
                  Excelência em Cada Detalhe
                </h3>

                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  A{" "}
                  <span className="font-bold text-sd-orange">
                    SD Serviços e Manutenções
                  </span>{" "}
                  é uma empresa especializada em oferecer soluções completas
                  para seu lar e negócio.
                </p>

                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Com profissional experiente e qualificado, garantimos serviços
                  de qualidade em diversas áreas como elétrica, hidráulica,
                  construção civil, marcenaria e muito mais.
                </p>

                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Nossa missão é proporcionar tranquilidade e satisfação aos
                  nossos clientes, realizando cada trabalho com dedicação,
                  pontualidade e excelência.
                </p>

                {/* Diferenciais em Lista */}
                <div className="space-y-3">
                  {[
                    "Atendimento personalizado",
                    "Orçamento sem compromisso",
                    "Serviço com Garantia",
                    "Profissional qualificado",
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-sd-orange/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg
                          className="w-4 h-4 text-sd-orange"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-gray-700 font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <StatCard key={index} value={stat.value} label={stat.label} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default About;